import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { motion, AnimatePresence } from 'motion/react'
import type { OverpaymentComparison } from '@/lib/mortgage'
import { formatPLN } from '@/lib/constants'

interface OverpaymentSummaryProps {
  comparison: OverpaymentComparison
  monthlyOverpayment: number
  baseMonthlyPayment: number
}

function formatTimeSaved(months: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  if (years === 0) return t('overpayment.onlyMonths', { months: remainingMonths })
  if (remainingMonths === 0) return t('overpayment.years', { years })
  return t('overpayment.yearsAndMonths', { years, months: remainingMonths })
}

export function OverpaymentSummary({
  comparison,
  monthlyOverpayment,
  baseMonthlyPayment,
}: OverpaymentSummaryProps) {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {monthlyOverpayment > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <Card className="border-0 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-emerald-400/10 to-transparent rounded-bl-full" />
            <CardContent className="py-5 px-5 relative">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">💰</span>
                <h2 className="text-sm font-bold text-emerald-800 uppercase tracking-wider">
                  {t('overpayment.title')} +{formatPLN(monthlyOverpayment)}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Interest Saved */}
                <div className="flex items-center justify-between sm:block sm:text-center">
                  <p className="text-xs text-emerald-600 sm:mb-1">{t('overpayment.interestSaved')}</p>
                  <p className="text-lg sm:text-xl font-bold text-emerald-700 tabular-nums">
                    {formatPLN(comparison.interestSaved)}
                  </p>
                </div>

                {/* Time Saved */}
                <div className="flex items-center justify-between sm:block sm:text-center">
                  <p className="text-xs text-emerald-600 sm:mb-1">{t('overpayment.timeSaved')}</p>
                  <p className="text-lg sm:text-xl font-bold text-emerald-700 tabular-nums">
                    {formatTimeSaved(comparison.monthsSaved, t)}
                  </p>
                </div>

                {/* New Monthly Total */}
                <div className="flex items-center justify-between sm:block sm:text-center">
                  <p className="text-xs text-emerald-600 sm:mb-1">{t('overpayment.newTotal')}</p>
                  <p className="text-lg sm:text-xl font-bold text-emerald-700 tabular-nums">
                    {formatPLN(baseMonthlyPayment + monthlyOverpayment)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
